<script setup lang="ts">
  import { MaybeElementRef, onClickOutside } from '@vueuse/core'
  import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
  import Moveable, {
    BoundType,
    MoveableRefTargetType,
    OnDrag,
    OnDragEnd,
    OnDragStart,
    OnResize,
    OnResizeEnd,
    OnResizeStart,
    OnScale,
    OnScaleStart,
    SnapDirections,
  } from 'vue3-moveable'
  import { ELEMENTS_LOOKUP, OLD_PAGE_MARGIN } from '../..'
  import { useMovable } from '../../composition/useMovable'
  import { MovableComponentEvents, MoveableComponentDirections } from '../../types'
  import { calculatePercentage, parseTransformValues } from '../../utils'

  const emit = defineEmits<{
    (
      event: MovableComponentEvents.ON_DRAG,
      payload: {
        top: number
        left: number
        height: number
        width: number
        bottom: number
      },
      target: HTMLElement
    ): void
    (event: MovableComponentEvents.ON_RESIZE, payload: { width: number; height: number; transform: string }, target: HTMLElement): void
    (event: MovableComponentEvents.ON_SCALE, scale: { scaleX: number; scaleY: number }, target: HTMLElement): void
    (event: MovableComponentEvents.ON_COPY): void
    (event: MovableComponentEvents.ON_DELETE): void
    (event: MovableComponentEvents.ON_ACTIVE, e: MouseEvent | any, isActive: boolean): void
    (event: MovableComponentEvents.ON_MOUSE_ENTER): void
    (event: MovableComponentEvents.ON_MOUSE_LEAVE): void
    (event: MovableComponentEvents.ON_FOCUS, isFocused: boolean): void
    (event: MovableComponentEvents.ON_DRAG_UPDATE, isDragging: boolean): void
    (
      event: MovableComponentEvents.ON_DRAG_END,
      options: {
        elementId: string
        newParentId: string
        sectionType?: string | null
        styles: {
          top: number
          left: number
          bottom: number
          right: number
          height: number
          width: number
          preferBottom?: boolean
          preferRight?: boolean
        }
      }
    ): void
    (event: MovableComponentEvents.ON_CLICK_ELEMENT, e: MouseEvent): void
    (event: MovableComponentEvents.ON_DRAG_START, e: OnDragStart): void
  }>()
  const props = withDefaults(
    defineProps<{
      pageIndex?: string
      pageId?: string
      snapDirections?: SnapDirections
      elementSnapDirections?: SnapDirections
      isActiveElement: boolean
      keepRatio?: boolean
      snapContainer?: string
      edgeDraggable?: boolean
      snappable?: boolean
      snapThreshold?: number
      position: {
        top: number
        left: number
        bottom: number
        preferBottom?: boolean
        right: number
        preferRight?: boolean
      }
      scale: { scaleX: number; scaleY: number }
      dimensions?: { height: number | string; width: number | string }
      id: string
      isGhost: boolean
      isEditable: boolean
      isDragElement: boolean
      align?: 'left' | 'right' | 'center'
      resizable?: boolean
      renderDirections?: MoveableComponentDirections[]
      minHeight?: number
      minWidth?: number
      maxHeight?: number
      maxWidth?: number
      colorOverride?: string
      inactiveColorOverride?: string
      useMinHeight?: boolean
      ignoreOnClick?: (MaybeElementRef | string)[]
      withDoubleClick?: boolean
      isSelectoField?: boolean
      elementGroup?: any
      disableNestedMoveable?: boolean
      boundContainer: string
      enableActiveEventOnDrag?: boolean
    }>(),
    {
      isDragElement: false,
      isEditable: false,
      isGhost: false,
      isBlockElement: true,
      keepRatio: true,
      snapContainer: '.snapContainer',
      boundContainer: '.snapContainer',
      edgeDraggable: false,
      snappable: true,
      snapThreshold: 5,
      resizable: false,
      colorOverride: '#4af',
      inactiveColorOverride: '#4af',
      renderDirections: () => {
        // return ['nw', 'ne', 'se', 'sw']
        return ['se']
      },
      ignoreOnClick: () => [],
      withDoubleClick: false,
      isSelectoField: false,
      disableNestedMoveable: false,
      enableActiveEventOnDrag: true,
    }
  )

  const directions = computed(() => ({
    top: true,
    left: true,
    bottom: true,
    right: true,
  }))
  const bounds = computed(() => {
    if (!props.boundContainer) {
      return {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        position: 'css',
      } as BoundType
    }

    const container = document.querySelector(props.boundContainer)
    if (!container) {
      return {
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        position: 'css',
      } as BoundType
    }

    // Return bounds that allow dragging within the container
    // The bounds are relative to the container, so we return 0 for all sides
    // which means the element can move anywhere within the container
    return {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      position: 'css',
    } as BoundType
  })
  // eslint-disable-next-line vue/no-dupe-keys
  const elementSnapDirections = computed(() => {
    return {
      top: true,
      left: true,
      bottom: true,
      right: true,
      center: true,
      middle: true,
    }
  })

  const { setMovableRef, setTooltipRef } = useMovable()
  const wrapperRef = ref()
  const isDoubleClicked = ref<boolean>(false)
  const isActive = ref<boolean>(true)
  const targetRef = ref<HTMLElement>()
  const tooltipRef = ref<HTMLElement>()
  const moveableRef = ref<any>()
  const ableRef = ref()
  const groupRef = ref()
  const positionRef = ref(
    props.position || {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      preferBottom: false,
      preferRight: false,
    }
  )
  const scaleRef = ref(props.scale || { scaleX: 1, scaleY: 1 })
  const isClicked = ref(false)
  const isDragging = ref(false)
  const isResizing = ref(false)
  const setTargetRef = () => {
    isClicked.value = true
  }
  const enablePopup = ref(true)

  const unsetTargetRef = () => {
    isClicked.value = false
    isDoubleClicked.value = false
    emit(MovableComponentEvents.ON_FOCUS, false)
  }

  watch(
    props,
    updatedProps => {
      isActive.value = updatedProps.isActiveElement
      if (isActive.value) {
        setTargetRef()
      } else {
        unsetTargetRef()
        moveableRef.value?.updateRect()
      }
      positionRef.value = updatedProps.position
      scaleRef.value = updatedProps.scale
    },
    { immediate: true, deep: true }
  )

  const showPopup = () => {
    if (props?.isSelectoField && props?.elementGroup) {
      enablePopup.value = false
    } else {
      enablePopup.value = true
    }
  }

  const onKeyPress = (e: KeyboardEvent) => {
    if (isActive.value) {
      e.stopPropagation()
      let increment = 1
      if (e.ctrlKey) {
        increment = 4
      }
      if (e.code === 'ArrowUp' && e.shiftKey) {
        moveableRef.value.request(
          'draggable',
          {
            deltaY: -increment,
          },
          true
        )
      }
      if (e.code === 'ArrowDown' && e.shiftKey) {
        moveableRef.value.request(
          'draggable',
          {
            deltaY: increment,
          },
          true
        )
      }
      if (e.code === 'ArrowLeft' && e.shiftKey) {
        moveableRef.value.request(
          'draggable',
          {
            deltaX: -increment,
          },
          true
        )
      }
      if (e.code === 'ArrowRight' && e.shiftKey) {
        moveableRef.value.request(
          'draggable',
          {
            deltaX: increment,
          },
          true
        )
      }
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', onKeyPress)
    // Read the rendered rect from the DOM after the element is mounted. We previously used
    // `props.position.top` here, which silently mis-anchored bottom-anchored (preferBottom)
    // elements — `position.top` for those stores the bottom-from-top distance, not the actual
    // rendered top, so the overlay landed 15px above the element's BOTTOM instead of the TOP.
    // `nextTick` ensures the inline `top:` / `bottom:` from the parent slot has been applied
    // before we measure.
    nextTick(() => {
      const r = computeRectFromDom()
      if (r) setTooltipPosition(r)
    })

    onClickOutside(
      wrapperRef,
      (e: any) => {
        // The `#active-elements-drawer` entry in the `ignore` list below is
        // supposed to short-circuit this callback when the click landed inside
        // the StyleManager drawer, but vueuse evaluates `ignore` via
        // `event.composedPath().includes(el)` which fails for synthetic clicks
        // the browser fires on a `<label>`-wrapped `<input>` (Naive UI's
        // NRadioButton shape). That synthetic click has `detail === 0` and a
        // truncated composedPath that omits the drawer ancestor, so vueuse's
        // ignore silently misses. Without this guard, clicking the image-
        // properties Align radios was deactivating the active element and
        // closing the drawer. `closest()` walks the actual `parentElement`
        // chain and is not affected by the synthetic-click quirk.
        const target = e?.target as HTMLElement | null
        if (target?.closest?.('#active-elements-drawer')) {
          return
        }
        unsetTargetRef()
        emit(MovableComponentEvents.ON_ACTIVE, e, false)
      },
      {
        capture: false,
        ignore: [
          `.v-binder-follower-container`,
          '.moveable-wrapper--ables',
          '#active-elements-drawer',
          '.custom-popover-options',
          '.v-binder-follower-content',
          ...props.ignoreOnClick,
        ],
      }
    )
    setMovableRef(props.id, moveableRef)
    setTooltipRef(props.id, tooltipRef)
    showPopup()
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', onKeyPress)
  })

  const onClick = (e: MouseEvent) => {
    e?.stopPropagation()
    if (!props.isDragElement) {
      return
    }

    if (props.isEditable) {
      showPopup()
      setTargetRef()
      emit(MovableComponentEvents.ON_ACTIVE, e, true)
      emit(MovableComponentEvents.ON_CLICK_ELEMENT, e)
      if (isActive.value && isClicked.value) {
        onDoubleClick()
      }
    }
    adjustAblePosition()
  }

  const adjustAblePosition = () => {
    // Read the rendered rect from the DOM (works identically for top- and bottom-anchored
    // elements). Falls back to `moveableRef.getRect()` only when targetRef isn't ready yet.
    const r = computeRectFromDom()
    if (r) {
      setTooltipPosition(r)
      return
    }
    if (moveableRef.value) {
      const { top, left, height, width } = moveableRef.value.getRect()
      setTooltipPosition({ top, left, height, width })
    }
  }
  const offsetX = ref(0)
  const offsetY = ref(0)
  const initialParentContainerType = ref<string | null>(null)
  const onDrag = (e: OnDrag) => {
    e.target.style.width = `${e.width}px`
    e.target.style.height = `${e.height}px`
    e.target.style.transform = e.transform
    if (props.enableActiveEventOnDrag) {
      emit(MovableComponentEvents.ON_ACTIVE, e, true)
    }

    if (!props.disableNestedMoveable) {
      clone.value?.style.setProperty('opacity', '0.6')
      clone.value?.style.setProperty('width', `${e.width}px`)
      clone.value?.style.setProperty('left', `${e.clientX + window.scrollX - offsetX.value}px`)
      clone.value?.style.setProperty('top', `${e.clientY + window.scrollY - offsetY.value}px`)
      clone.value?.style.setProperty('height', `${e.height}px`)
    }
    const elementRect = e.target.getBoundingClientRect()
    offsetX.value = e.clientX - elementRect.left
    offsetY.value = e.clientY - elementRect.top
    e.moveable.forceUpdate()
  }

  const clone = ref<HTMLElement | null>(null)

  const removeAllClones = () => {
    const elements = document.querySelectorAll('.moveable-clone')
    elements.forEach(element => {
      element?.remove()
    })
  }
  const onDragStart = (e: OnDragStart) => {
    if (!props.disableNestedMoveable) {
      e?.inputEvent?.stopImmediatePropagation()
      removeAllClones()
      const parentStyle = e.target.parentElement?.style
      clone.value = e.target.cloneNode(true) as HTMLElement
      clone.value.style.cssText = parentStyle?.cssText as string
      clone.value.classList.add('moveable-clone')
      clone.value.style.opacity = '0'
      document.body.appendChild(clone.value)
    }
    emit(MovableComponentEvents.ON_DRAG_START, e)
    emit(MovableComponentEvents.ON_DRAG_UPDATE, true)
    isDragging.value = true
    if (!props.disableNestedMoveable) {
      const parentInfo = getNearestSnap(e.target as HTMLElement)
      initialParentContainerType.value = parentInfo.type
    }
    offsetX.value = e.clientX - e.target.getBoundingClientRect().left
    offsetY.value = e.clientY - e.target.getBoundingClientRect().top
  }

  const getNearestSnap = (targetElement: HTMLElement) => {
    // Prefer a single snap root (e.g. `.snapContainer-0-section-footers`). A bare
    // `.snapContainer-0-section` matches header + footer; `querySelector` would be ambiguous.
    const snapRoot = document.querySelector(props.snapContainer)
    const divs = snapRoot?.querySelectorAll('.boundBox') ?? document.querySelectorAll(`${props.snapContainer} .boundBox`)

    let nearestDiv: HTMLElement | null = null
    let nearestSectionDiv: HTMLElement | null = null // Prioritize section containers

    const targetRect = targetElement.getBoundingClientRect()
    const { top: targetTop, left: targetLeft, bottom: targetBottom, right: targetRight } = targetRect

    const findNearestDiv = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect()

      // Check if the top-left corner of the target element is within this element
      const isInside = targetLeft >= rect.left && targetTop + 1 >= rect.top && rect.bottom >= targetBottom + 1 && rect.right >= targetRight

      if (isInside) {
        // Check if this element has a section-type attribute (header/footer section)
        const sectionType = element.getAttribute('data-section-type')
        if (sectionType) {
          // Prioritize section containers over page containers
          nearestSectionDiv = element
        } else {
          nearestDiv = element
        }
        element.querySelectorAll(`${props.snapContainer}`).forEach(child => findNearestDiv(child as HTMLElement))
      }
    }

    divs.forEach(div => {
      if (div && div.style) {
        div.style.outline = 'none'
      }
      findNearestDiv(div as HTMLElement)
    })

    // Use section container if found, otherwise use regular container
    if (nearestSectionDiv) {
      nearestDiv = nearestSectionDiv
    } else if (!nearestDiv) {
      // Try to find the bound container directly (for sections)
      const boundContainer = document.querySelector(props.boundContainer)
      if (boundContainer) {
        const sectionType = (boundContainer as HTMLElement)?.getAttribute('data-section-type')
        if (sectionType) {
          nearestDiv = boundContainer as HTMLElement
        } else {
          nearestDiv = document.querySelector(`${props.snapContainer}`)
        }
      } else {
        nearestDiv = document.querySelector(`${props.snapContainer}`)
      }
    }

    const id = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-id')
    const type = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-type')
    const pageId = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-page-id')
    const sectionType = (nearestDiv as unknown as HTMLElement)?.getAttribute('data-section-type')
    /**
   * @description This code is only for debugging purpose. DO NO REMOVE
   * Highlight the page element when the target element is a page
   *

  if (nearestDiv && (nearestDiv as HTMLElement).style) {
    nearestDiv.style.outlineOffset = '-1px'
    nearestDiv.style.borderRadius = '3px'
    nearestDiv.style.outline = '1px dashed #fad1d1'
  }

  if (
    type === ELEMENTS_LOOKUP.PAGE &&
    (nearestDiv as unknown as HTMLElement).style
  ) {
    nearestDiv.style.outline = '1px dashed #e4fad1'
    nearestDiv.style.outlineOffset = '-1px'
    nearestDiv.style.borderRadius = '3px'
  }
  **/
    return {
      id,
      type,
      pageId,
      sectionType,
      div: nearestDiv as unknown as HTMLDivElement,
    }
  }

  const checkIfPreferBottom = (target: HTMLElement, topPosition: number) => {
    const rect = target?.getBoundingClientRect()
    if (!rect) {
      return false
    }
    const percentage = calculatePercentage(rect?.height, 60)
    return topPosition >= percentage
  }

  const checkIfPreferRight = (target: HTMLElement, leftPosition: number) => {
    const rect = target?.getBoundingClientRect()
    if (!rect) {
      return false
    }
    const percentage = calculatePercentage(rect?.width, 50)
    return leftPosition >= percentage
  }

  const onDragEnd = (event: OnDragEnd) => {
    removeAllClones()
    clone.value?.remove()

    if (event.lastEvent) {
      const { target, top, left, height, width, bottom, right } = event.lastEvent
      if (!props.disableNestedMoveable) {
        // Use the dragged element from lastEvent — `event.target` on dragEnd is often `document.body`
        // or an unrelated node, which makes getNearestSnap pick the wrong header/footer section.
        const parentInfo = getNearestSnap(target as HTMLElement)

        // For section elements, use the bound container directly to ensure correct positioning
        let containerDiv: HTMLElement | null = parentInfo.div
        if (parentInfo.sectionType) {
          // Try to find the bound container which should be the section container
          const boundContainer = document.querySelector(props.boundContainer)
          if (boundContainer) {
            const boundSectionType = (boundContainer as HTMLElement)?.getAttribute('data-section-type')
            // If bound container matches the section type, use it
            if (boundSectionType === parentInfo.sectionType) {
              containerDiv = boundContainer as HTMLElement
            }
          }
        }

        const rect = containerDiv?.getBoundingClientRect()

        // Detect whether the dragged element's CSS containing block (its positioned
        // ancestor / `offsetParent`) is a DESCENDANT of the snap target. This is the bug
        // condition for the else-branch below: if the element is *structurally* nested
        // inside a positioned inner block (e.g. a Text block's `<section>` boundBox with
        // `transform: translateZ(0)`) but `getNearestSnap`'s strict `isInside` check fell
        // back to PAGE (because the element's rect extends past the inner block's rect),
        // moveable reports `top` / `left` / `bottom` / `right` relative to the INNER
        // ancestor, NOT the snap target. The else-branch trusts moveable's coords and
        // stores them as snap-target-relative — which writes inner-block-relative values
        // into page-level storage and snaps the element to the top of the page.
        //
        // When this mismatch is detected, force the if-branch (clientY/rect math) so
        // coordinates are computed in the snap target's frame regardless of CSS nesting.
        // The if-branch then needs the same OLD_PAGE_MARGIN +48 conversion that the
        // nested-to-page path already uses (see the compensation gate below).
        const draggedTarget = target as HTMLElement | null
        const offsetParent = draggedTarget?.offsetParent as HTMLElement | null
        const offsetParentIsInsideSnapTarget = Boolean(
          offsetParent && containerDiv && offsetParent !== containerDiv && containerDiv.contains(offsetParent)
        )
        let snappedTop = 0
        let snappedRight = 0
        let snappedLeft = 0
        let snappedBottom = 0
        // If there's a sectionType, always calculate relative to the container
        // This ensures correct positioning for header/footer sections.
        // `offsetParentIsInsideSnapTarget` forces the if-branch when the dragged
        // element is structurally nested inside an inner positioned ancestor (see
        // comment above) — without it, moveable's inner-ancestor-relative coords
        // would leak into snap-target-relative storage.
        if (
          parentInfo.sectionType ||
          parentInfo.type !== ELEMENTS_LOOKUP.PAGE ||
          initialParentContainerType.value !== ELEMENTS_LOOKUP.PAGE ||
          offsetParentIsInsideSnapTarget
        ) {
          if (!rect) {
            // Fallback: use parentInfo.div if containerDiv doesn't have rect
            const fallbackRect = parentInfo.div?.getBoundingClientRect()
            snappedTop = event?.inputEvent?.clientY - (fallbackRect?.top || 0) + (parentInfo.div?.scrollTop || 0) - offsetY.value
            snappedLeft = event?.inputEvent?.clientX - (fallbackRect?.left || 0) + (parentInfo.div?.scrollLeft || 0) - offsetX.value
            snappedBottom = (fallbackRect?.height || 0) - (snappedTop + height)
            snappedRight = (fallbackRect?.width || 0) - (snappedLeft + width)
          } else {
            snappedTop = event?.inputEvent?.clientY - (rect?.top || 0) + (containerDiv?.scrollTop || 0) - offsetY.value
            snappedLeft = event?.inputEvent?.clientX - (rect?.left || 0) + (containerDiv?.scrollLeft || 0) - offsetX.value
            snappedBottom = rect?.height - (snappedTop + height)
            snappedRight = rect?.width - (snappedLeft + width)
          }
        } else {
          snappedTop = top
          snappedLeft = left
          snappedBottom = bottom
          snappedRight = right
        }

        // OLD_PAGE_MARGIN (+48) compensates for legacy page-relative storage on body content:
        // when the drag math was computed against `.page-content` but the destination is
        // page-level (v1/v2 stored floats anchored to `.page-view`, which sits 48px outside
        // `.page-content` due to page margins), this shift converts content-relative coords
        // back to page-relative coords. Body keeps this behavior unchanged.
        //
        // H/F sections do not need this compensation: the section is its own coordinate
        // space (no analog of the page's 48px margin band) and the math already produces
        // section-relative coordinates that match where Moveable visibly placed the float.
        // Without this gate, dropping inside an H/F section from a nested block snap
        // (Text → fallback Page) shifts the stored position 48px on every side, so the
        // rendered element ends up offset up/left from the bound box (the drop point).
        if (
          (initialParentContainerType.value !== ELEMENTS_LOOKUP.PAGE || offsetParentIsInsideSnapTarget) &&
          parentInfo.type === ELEMENTS_LOOKUP.PAGE &&
          !parentInfo.sectionType
        ) {
          // The if-branch above produced page-content-relative coords. The compensation
          // adds the page margin to convert to page-view-relative (legacy v1/v2 storage
          // frame). Two paths reach this:
          //  - `initialParentContainerType !== PAGE`: classic nested-block-to-page drop.
          //  - `offsetParentIsInsideSnapTarget`: drag-start snap fell back to PAGE because
          //    the element's rect didn't fit its parent's `.boundBox`, but the element is
          //    still structurally nested inside that parent's transform-context. We forced
          //    the if-branch above, so we also need the +48 here so the stored coords
          //    match the renderer's page-view-relative origin.
          snappedBottom = snappedBottom + OLD_PAGE_MARGIN
          snappedLeft = snappedLeft + OLD_PAGE_MARGIN
          snappedRight = snappedRight + OLD_PAGE_MARGIN
          snappedTop = snappedTop + OLD_PAGE_MARGIN
        }
        // Section-scoped clamp for `bottom`. When dragging within an H/F section, the
        // position math can produce a negative `snappedBottom` if the user drops near the
        // section's bottom edge: `rect.height - (snappedTop + height)` goes negative because
        // the float's lower edge naturally extends past the cursor by `(height - offsetY)`.
        // If `preferBottom` is then true (drop in the lower 60% of the container), the
        // renderer uses `bottom: -Npx` and the float visibly jumps below the section.
        //
        // The top/left/right values already clamp negatives to 0 here; `bottom` was the only
        // outlier. Restrict this fix to section drags only — body content is unchanged.
        //
        // Detection: check the bound container's `data-section-type` rather than
        // `parentInfo.sectionType`. The latter is only set when the snap resolves to the
        // section ROOT; if the float ended up snapped to a nested block (e.g. a Text block
        // inside the header), `parentInfo.sectionType` is null even though we're still
        // dragging inside the H/F section. The bound container catches both cases because
        // PageSection always passes a section-scoped bound container selector down.
        const boundContainerEl = document.querySelector(props.boundContainer) as HTMLElement | null
        const isInsideHeaderFooterSection = !!boundContainerEl?.getAttribute('data-section-type')

        const dragEndPayload = {
          styles: {
            top: (snappedTop < -1 ? 0 : snappedTop) as number,
            left: (snappedLeft < 0 ? 0 : snappedLeft) as number,
            bottom: (isInsideHeaderFooterSection && snappedBottom < 0 ? 0 : snappedBottom) as number,
            right: (snappedRight < 0 ? 0 : snappedRight) as number,
            height: height as number,
            width: width as number,
            preferBottom: checkIfPreferBottom(parentInfo.div, snappedTop) as boolean,
            preferRight: checkIfPreferRight(parentInfo.div, snappedLeft) as boolean,
          },
          elementId: props.id as string,
          newParentId: parentInfo.id as string,
          sectionType: parentInfo.sectionType || null,
        }
        emit(MovableComponentEvents.ON_DRAG_END, dragEndPayload)
        offsetX.value = 0
        offsetY.value = 0
        moveableRef?.value?.updateRect()
        showPopup()
        adjustAblePosition()
      } else {
        emit(
          MovableComponentEvents.ON_DRAG,
          {
            top,
            left,
            height,
            width,
            bottom,
          },
          target
        )
        showPopup()
        adjustAblePosition()
      }
      moveableRef?.value?.updateRect()
    }
    isDragging.value = false
    emit(MovableComponentEvents.ON_DRAG_UPDATE, false)
  }

  const resizeDeltaX = ref(0)
  const resizeDeltaY = ref(0)
  const onResize = (e: OnResize) => {
    e.target.style.width = `${e.width}px`
    e.target.style.height = `${e.height}px`
    e.target.style.transform = e.drag.transform
    const deltaX = e.width - resizeDeltaX.value
    const deltaY = e.height - resizeDeltaY.value
    if (props.position.preferRight) {
      e.target.style.left = props.position.left + 'px'
      e.target.style.right = (deltaX > 0 ? props.position.right - Math.abs(deltaX) : props.position.right + Math.abs(deltaX)) + 'px'
    }
    if (props.position.preferBottom) {
      e.target.style.top = props.position.top + 'px'
      e.target.style.bottom = (deltaY > 0 ? props.position.bottom - Math.abs(deltaY) : props.position.bottom + Math.abs(deltaY)) + 'px'
    }

    emit(
      MovableComponentEvents.ON_RESIZE,
      {
        width: e.width,
        height: e.height,
        transform: e.drag.transform,
      },
      e.target
    )
  }

  const onScale = (e: OnScale) => {
    const { scale } = parseTransformValues(e.transform)
    emit(MovableComponentEvents.ON_SCALE, scale, e.target)
  }

  const styles = computed(() => {
    const stylesObj: Record<string, string> = {}
    if (scaleRef.value) {
      stylesObj['transform'] = `scale(${scaleRef.value.scaleX}, ${scaleRef.value.scaleY})`
    }
    if (positionRef.value) {
      if (Object.hasOwnProperty.call(positionRef.value, 'preferBottom') && positionRef.value.preferBottom) {
        stylesObj['bottom'] = `${positionRef.value?.bottom}px`
      } else {
        stylesObj['top'] = `${positionRef.value.top}px`
      }

      if (Object.hasOwnProperty.call(positionRef.value, 'preferRight') && positionRef.value.preferRight) {
        stylesObj['right'] = `${positionRef.value.right}px`
      } else {
        stylesObj['left'] = `${positionRef.value.left}px`
      }
      stylesObj['zIndex'] = '100'
    }
    if (props.dimensions) {
      if (props.dimensions.height !== 'auto') {
        stylesObj['height'] = `${props.dimensions.height}px`
      }
      if (props.dimensions.width !== 'auto') {
        stylesObj['width'] = `${props.dimensions.width}px`
      }
    }
    return stylesObj
  })
  const elementGuidelines = computed(() => ['.el-wrapper', 'section'])
  const onMouseEnter = () => {
    if (!isClicked.value) {
      isActive.value = true
    }
  }
  const onMouseLeave = () => {
    if (!props.disableNestedMoveable) {
      if (!isClicked.value) {
        isActive.value = false
      }
    }
  }
  const onScaleStart = (e: OnScaleStart) => {
    e.setMinScaleSize([15, 15])
  }
  const onResizeStart = (e: OnResizeStart) => {
    e.setMin([props.minWidth || 15, props.minHeight || 15])
    e.setMax([props.maxWidth || '100%', props.maxHeight || '100%'])
    isResizing.value = true
    resizeDeltaX.value = e.moveable.getRect().width
    resizeDeltaY.value = e.moveable.getRect().height
  }
  const onResizeEnd = (e: OnResizeEnd) => {
    isResizing.value = false
    const { height, top, left, width } = e.moveable.getRect()
    const deltaX = width - resizeDeltaX.value
    const deltaY = height - resizeDeltaY.value
    const right = deltaX > 0 ? props.position.right - Math.abs(deltaX) : props.position.right + Math.abs(deltaX)

    const bottom = deltaY > 0 ? props.position.bottom - Math.abs(deltaY) : props.position.bottom + Math.abs(deltaY)

    e.target.style.removeProperty('inset')
    if (props.position.preferRight) {
      e.target.style.right = right + 'px'
    } else {
      e.target.style.left = props.position.left + 'px'
    }
    if (props.position.preferBottom) {
      e.target.style.bottom = bottom + 'px'
    } else {
      e.target.style.top = props.position.top + 'px'
    }
    // Re-measure from the DOM after the post-resize inline styles have been applied so the
    // overlay anchors to the final rendered rect (works for top- and bottom-anchored elements).
    // Without this, preferBottom elements end up with a stale overlay position because
    // `getRect()` was called BEFORE we wrote the final `bottom:` style.
    nextTick(() => {
      const r = computeRectFromDom()
      if (r) setTooltipPosition(r)
      else setTooltipPosition({ top, left, height, width })
    })
    emit(
      MovableComponentEvents.ON_DRAG,
      {
        ...props.position,
        right,
        bottom,
      },
      e.target
    )
    resizeDeltaX.value = 0
    resizeDeltaY.value = 0
    offsetX.value = 0
    offsetY.value = 0
    e.moveable.forceUpdate()
  }

  const onDoubleClick = () => {
    if (isActive.value && targetRef.value) {
      const textareaOrInput = targetRef.value.querySelector('textarea')
      textareaOrInput?.focus()
      emit(MovableComponentEvents.ON_FOCUS, true)
      isDoubleClicked.value = true
    }
  }

  const setTooltipPosition = ({ top, left, height, width }: { top: number; height: number; left: number; width: number }) => {
    if (tooltipRef.value) {
      tooltipRef.value.style.top = `${top + height + 15}px`
      tooltipRef.value.style.left = `${left + 15}px`
    }
    if (ableRef.value) {
      ableRef.value.style.top = `${top - 15}px`
      ableRef.value.style.right = `calc(100% - ${left + width}px)`
    }

    if (groupRef.value) {
      groupRef.value.style.top = `${top + height + 2}px`
      groupRef.value.style.right = `calc(100% - ${left + width / 2 + 15}px)`
    }
  }
  /**
   * DOM-derived rect for the target element, expressed in the same coordinate space as
   * `targetRef`'s sibling overlay refs (`ableRef`, `tooltipRef`, `groupRef`) — i.e. relative
   * to the shared `offsetParent`. Reading from the DOM (instead of `props.position.top` or
   * `moveableRef.getRect()`) makes this work identically for top-anchored AND bottom-anchored
   * (`preferBottom`) elements, because we're reading the RENDERED top — which the browser has
   * already computed regardless of whether the inline style uses `top:` or `bottom:`. With
   * `props.position.top`, preferBottom elements ended up with the overlay anchored 15px above
   * the element's BOTTOM (because `position.top` for those records stores the bottom-from-top
   * distance), which is exactly the misplacement we were debugging.
   */
  const computeRectFromDom = () => {
    if (!targetRef.value) return null
    const elRect = targetRef.value.getBoundingClientRect()
    const op = targetRef.value.offsetParent as HTMLElement | null
    const opRect = op?.getBoundingClientRect()
    return {
      top: elRect.top - (opRect?.top ?? 0),
      left: elRect.left - (opRect?.left ?? 0),
      width: elRect.width,
      height: elRect.height,
    }
  }
  const showBorders = computed(() => {
    return isActive.value || isClicked.value ? 'unset' : 'none'
  })
</script>

<template>
  <div
    v-if="props.isDragElement"
    :id="`moveable-container-${id}`"
    :class="`moveable__container`"
    @focusin="onMouseEnter"
    @focusout="onMouseLeave"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    ref="wrapperRef"
  >
    <div
      :data-page-index="pageIndex"
      :data-page-id="pageId"
      :data-element-id="id"
      @click="onClick"
      ref="targetRef"
      :style="{
        ...styles,
        opacity: !isDragging ? 1 : 0.6,
      }"
      style="transform-origin': left top;"
      class="moveable-component--wrapper"
      :class="{
        'select-o-field': props.isSelectoField,
        'not-editable': !props.isEditable,
        'flex flex-1': true,
        absolute: isGhost,
        'cursor-move': props.isEditable,
        'shadow-float !cursor-move': isDragging,
        ...(props.withDoubleClick
          ? {
              'not-active': !isDoubleClicked,
            }
          : {
              'not-active': !isClicked,
            }),
      }"
    >
      <slot name="component" />
    </div>

    <div
      ref="ableRef"
      :style="{
        display: enablePopup && isClicked && !isDragging && !isResizing ? 'flex' : 'none',
        zIndex: 101,
        position: 'absolute',
        willChange: 'position',
      }"
      class="moveable-wrapper--ables actions absolute -mt-8 flex"
    >
      <slot name="popup-options" />
    </div>
    <div
      ref="tooltipRef"
      :class="{
        hidden: isClicked || isResizing || isDragging,
      }"
      :style="{
        zIndex: 101,
        position: 'absolute',
        willChange: 'position',
        transform: 'translateX(0%) translateY(100%)',
        transition: 'opacity 0.15s ease 0s',
        boxShadow: 'rgba(47, 47, 47, 0.04) 0px 0px 2px 0px, rgba(47, 47, 47, 0.12) 0px 2px 8px 0px',
        whiteSpace: 'nowrap',
      }"
      class="moveable--tooltip bg-white actions absolute -mt-8 flex"
    >
      <div class="flex justify-between rounded-sm animate-slideDown">
        <slot name="tooltip" />
      </div>
    </div>

    <div
      ref="groupRef"
      :style="{
        display: enablePopup && isClicked && !isDragging && !isResizing ? 'flex' : 'none',
        zIndex: 101,
        position: 'absolute',
        willChange: 'position',
      }"
      class="moveable-wrapper--ables flex"
    >
      <slot name="group-options" />
    </div>
    <Moveable
      :key="`moveable-component-${id}`"
      ref="moveableRef"
      :class-name="`moveable ${isClicked || isActiveElement ? 'ghl-block' : 'ghl-none'}`"
      :target="targetRef as MoveableRefTargetType"
      :draggable="isEditable && isActive"
      :throttle-drag="1"
      :scalable="isEditable && isClicked"
      :snap-container="props.snapContainer"
      :root-container="props.boundContainer || props.snapContainer"
      :edge-draggable="false"
      :throttle-scale="0"
      :snappable="true"
      :snap-gap="true"
      :snap-directions="directions"
      :snap-threshold="props.snapThreshold"
      :bounds="bounds"
      :element-snap-directions="elementSnapDirections"
      :element-guidelines="elementGuidelines"
      :is-display-snap-digit="false"
      :is-display-inner-snap-digit="false"
      :origin="false"
      :keep-ratio="props.keepRatio"
      :use-accurate-position="true"
      :resizable="isEditable && resizable"
      @drag="onDrag"
      @resize="onResize"
      @scale="onScale"
      @scaleStart="onScaleStart"
      @resizeStart="onResizeStart"
      @resizeEnd="onResizeEnd"
      @dragEnd="onDragEnd"
      @dragStart="onDragStart"
      :render-directions="renderDirections as string[]"
    />
  </div>
  <slot v-else name="component" />
</template>

<style lang="scss">
  .moveable__container {
    .moveable-component--wrapper {
      &.not-active {
        .el-wrapper {
          &::before {
            position: absolute;
            content: '';
            background-color: transparent;
            cursor: pointer;
            height: 100%;
            width: 100%;
            z-index: 102;
          }
        }
      }
    }
    .moveable-control-box {
      z-index: 100 !important;
      &.ghl-none {
        .moveable-line {
          background: v-bind('props.inactiveColorOverride') !important;
        }
      }
      .moveable-line {
        background: v-bind('props.colorOverride') !important;
        height: 1.5px;
        width: 1.5px;
        display: v-bind('showBorders');
      }
      .moveable-control.moveable-direction.moveable-resizable {
        background: v-bind('props.colorOverride') !important;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25);
      }
      &.ghl-block {
        .moveable-control.moveable-direction.moveable-resizable {
          display: block !important;
        }
      }
      &.ghl-none {
        .moveable-control.moveable-direction.moveable-resizable {
          display: none !important;
        }
      }
    }
    &:hover {
      .moveable-control-box.ghl-none {
        .moveable-control.moveable-direction.moveable-resizable {
          display: block !important;
        }
      }
    }
    .moveable--tooltip {
      opacity: 0;
      pointer-events: none;
    }
    &:hover {
      .moveable--tooltip {
        opacity: 1;
        pointer-events: all;
      }
    }
  }
</style>
